/* eslint-disable @typescript-eslint/naming-convention */
import { SystemEnvironment } from '@/constants/systemEnviroment';
import { getSystemEnvironment } from '@/utils/system-environment';

enum environments {
  LOCAL = 'local',
  DEV = 'dev',
  STAGE = 'stage',
  PROD = 'prod',
}

const { PROD, STAGE, LOCAL, DEV } = environments;

export const isDev: boolean =
  window.global.REACT_APP_ENVIRONMENT === 'development';
export const isProduction: boolean =
  window.global.REACT_APP_ENVIRONMENT === 'production';
export const isStaging: boolean =
  window.global.REACT_APP_ENVIRONMENT === 'staging';
export const isLocal: boolean = window.global.REACT_APP_ENVIRONMENT === 'local';

export const isMultiEnvEnabled = () => {
  return isDev || isLocal;
};

export const environment: string = (
  isMultiEnvEnabled()
    ? getSystemEnvironment() === SystemEnvironment.PRODUCTION
      ? PROD
      : STAGE
    : isProduction
      ? PROD
      : isStaging
        ? STAGE
        : isDev
          ? DEV
          : LOCAL
) as string;

export const domainBaseEnvironment: string = (
  isProduction ? PROD : isStaging ? STAGE : isDev ? DEV : LOCAL
) as string;

export const isAppLocalMode: boolean =
  process.env.WEB_APP_LOCAL_MODE === 'true';

const PLANNER_LOCAL_SERVER_URL = '';
const STAGING_SERVER_URL = window.global.REACT_APP_STAGING_SERVER_URL;
const PRODUCTION_SERVER_URL = window.global.REACT_APP_PRODUCTION_SERVER_URL;
const REACT_APP_RELEASE_STAGE = window.global.REACT_APP_RELEASE_STAGE ?? '';
export const REACT_APP_TENANT_SEQ = window.global.REACT_APP_TENANT_SEQ ?? '1';

export { REACT_APP_RELEASE_STAGE };

const LOCAL_SERVER_URL =
  process.env.WEB_APP_DOCKER_MODE === 'true'
    ? 'http://localhost:8090'
    : isAppLocalMode
      ? PLANNER_LOCAL_SERVER_URL
      : STAGING_SERVER_URL;
const LOCAL_SAML_SERVER_URL =
  process.env.WEB_APP_DOCKER_MODE === 'true'
    ? 'http://localhost:8088'
    : STAGING_SERVER_URL;

// these client IDs are generated in Google OAuth, then whitelisted by #grab-id
const STAGING_GOOGLE_CLIENT_ID =
  '948387837074-a1s4dmkb4kl36bht8ea2qgjle9ft1bj7';
const DEV_GOOGLE_CLIENT_ID = '72540351358-e53utl40ebc3c9hj7mprd621v8u3qs5r';
const PROD_GOOGLE_CLIENT_ID = '948387837074-avmc15sdr7gedrgqbv86otarchem3jq5';

const STAGING_GRAB_AREA_MANAGEMENT_URL =
  'https://grab-area-management.stg.mngd.int.engtools.net';
const PRODUCTION_GRAB_AREA_MANAGEMENT_URL =
  'https://grab-area-management.mngd.int.engtools.net';

const getValueFromMap = function (
  inputMap: { local: string; dev: string; stage: string; prod: string },
  environmentString: string,
): string {
  return inputMap[environmentString] ?? inputMap[LOCAL];
};

export const GOOGLE_CLIENT_ID = `${
  {
    [LOCAL]: STAGING_GOOGLE_CLIENT_ID,
    [DEV]: DEV_GOOGLE_CLIENT_ID,
    [STAGE]: STAGING_GOOGLE_CLIENT_ID,
    [PROD]: PROD_GOOGLE_CLIENT_ID,
  }[domainBaseEnvironment]
}.apps.googleusercontent.com`;

export const CONCEDO_IDC_URL = getValueFromMap(
  {
    [LOCAL]: `https://api.stg-myteksi.com/concedoidc`,
    [DEV]: `https://api.stg-myteksi.com/concedoidc`,
    [STAGE]: `https://api.stg-myteksi.com/concedoidc`,
    [PROD]: `https://api.grab.com/concedoidc`,
  },
  domainBaseEnvironment,
);

export const CONCEDO_CLIENT_ID = getValueFromMap(
  {
    [LOCAL]: `grabx_stg`,
    [DEV]: `grabx_stg`,
    [STAGE]: `grabx_stg`,
    [PROD]: `grabx_planner`,
  },
  domainBaseEnvironment,
);

export const CONCEDO_MOVEIT_CLIENT_ID = getValueFromMap(
  {
    [LOCAL]: `grabx_stg_moveit_planner`,
    [DEV]: `grabx_stg_moveit_planner`,
    [STAGE]: `grabx_stg_moveit_planner`,
    [PROD]: `grabx_moveit_planner`,
  },
  domainBaseEnvironment,
);

export const CONCEDO_CHOPE_CLIENT_ID = getValueFromMap(
  {
    [LOCAL]: `grabx_stg_chope_planner`,
    [DEV]: `grabx_stg_chope_planner`,
    [STAGE]: `grabx_stg_chope_planner`,
    [PROD]: `grabx_chope_planner`,
  },
  domainBaseEnvironment,
);

export const CONCEDO_OVO_CLIENT_ID = getValueFromMap(
  {
    [LOCAL]: `grabx_stg_ovo_planner`,
    [DEV]: `grabx_stg_ovo_planner`,
    [STAGE]: `grabx_stg_ovo_planner`,
    [PROD]: `grabx_ovo_planner`,
  },
  domainBaseEnvironment,
);

export const CONCEDO_MEXLITE_CLIENT_ID = getValueFromMap(
  {
    [LOCAL]: `grabx_stg_mexlite_planner`,
    [DEV]: `grabx_stg_mexlite_planner`,
    [STAGE]: `grabx_stg_mexlite_planner`,
    [PROD]: `grabx_mexlite_planner`,
  },
  domainBaseEnvironment,
);

// these client IDs are generated in Google OAuth, then whitelisted by #grab-id
const STAGING_MOVEIT_GOOGLE_CLIENT_ID =
  '72540351358-midn28a8eegkchrho9a6q92l6te4eaqj';
const PRODUCTION_MOVEIT_GOOGLE_CLIENT_ID =
  '72540351358-r7n9ks6cbok0vedqpdkq2ddsp4j6r54h';

export const USER_FEEDBACK_COMPONENT_ENV = getValueFromMap(
  {
    [LOCAL]: 'dev',
    [DEV]: 'stg', // get stg campaign for dev env
    [STAGE]: 'prd', // send stg feedbacks onto prd env so the results would be available on superset
    [PROD]: 'prd',
  },
  domainBaseEnvironment,
);

export const MOVEIT_GOOGLE_CLIENT_ID = `${
  {
    [LOCAL]: STAGING_MOVEIT_GOOGLE_CLIENT_ID,
    [DEV]: STAGING_MOVEIT_GOOGLE_CLIENT_ID,
    [STAGE]: STAGING_MOVEIT_GOOGLE_CLIENT_ID,
    [PROD]: PRODUCTION_MOVEIT_GOOGLE_CLIENT_ID,
  }[domainBaseEnvironment]
}.apps.googleusercontent.com`;

export const PLANNER_URL = getValueFromMap(
  {
    [LOCAL]: `${LOCAL_SERVER_URL}/${isAppLocalMode ? 'portal' : 'x-portal'}`,
    [DEV]: `${STAGING_SERVER_URL}/x-portal`,
    [STAGE]: `${STAGING_SERVER_URL}/x-portal`,
    [PROD]: `${PRODUCTION_SERVER_URL}/x-portal`,
  },
  domainBaseEnvironment,
);

export const GRAB_AREA_MANAGEMENT_URL = getValueFromMap(
  {
    [LOCAL]: `${STAGING_GRAB_AREA_MANAGEMENT_URL}/grabareamanagement`,
    [DEV]: `${STAGING_GRAB_AREA_MANAGEMENT_URL}/grabareamanagement`,
    [STAGE]: `${STAGING_GRAB_AREA_MANAGEMENT_URL}/grabareamanagement`,
    [PROD]: `${PRODUCTION_GRAB_AREA_MANAGEMENT_URL}/grabareamanagement`,
  },
  environment,
);

export const SAML_URL = getValueFromMap(
  {
    [LOCAL]: `${LOCAL_SAML_SERVER_URL}/saml`,
    [DEV]: `${STAGING_SERVER_URL}/saml`,
    [STAGE]: `${STAGING_SERVER_URL}/saml`,
    [PROD]: `${PRODUCTION_SERVER_URL}/saml`,
  },
  domainBaseEnvironment,
);

export const API_URL = getValueFromMap(
  {
    [LOCAL]: `${LOCAL_SERVER_URL}/api`,
    [DEV]: ``,
    [STAGE]: ``,
    [PROD]: ``,
  },
  domainBaseEnvironment,
);

export const TRANSMITTER_URL = getValueFromMap(
  {
    [LOCAL]: `${STAGING_SERVER_URL}/x-transmitter`,
    [DEV]: `${STAGING_SERVER_URL}/x-transmitter`,
    [STAGE]: `${STAGING_SERVER_URL}/x-transmitter`,
    [PROD]: `${PRODUCTION_SERVER_URL}/x-transmitter`,
  },
  environment,
);

export const redirectTo: string =
  new URLSearchParams(
    // Not sure if it should default to empty
    window.location.search,
  ).get('redirectTo') ?? '';

export const applicationName: string = 'experimentation-portal';

export const clientType: string = 'pax';

export const SEGP_WHITELIST_TOKEN_STG =
  'Z3JhYi14LXRva2VuLWZvci1zdGFnaW5nLWZvci1jYWxsaW5nLXRvLXVpLWJl';
export const SEGP_WHITELIST_TOKEN_PROD =
  'Z3JhYi14LXRva2VuLXByb2R1Y3Rpb24td2hlbi1jYWxsaW5nLXRvLVVJLUJF';

export const SEGP_WHITELIST_TOKEN =
  isProduction ||
  (isMultiEnvEnabled() &&
    getSystemEnvironment() === SystemEnvironment.PRODUCTION)
    ? SEGP_WHITELIST_TOKEN_PROD
    : SEGP_WHITELIST_TOKEN_STG;

export const SEGP_UI_BACKEND_URL_STG = 'https://segments-ui.stg-myteksi.com/v2';
export const SEGP_UI_BACKEND_URL_PROD = 'https://segments-ui.grab.com/v2';

export const SEGP_UI_BACKEND_URL =
  isProduction ||
  (isMultiEnvEnabled() &&
    getSystemEnvironment() === SystemEnvironment.PRODUCTION)
    ? SEGP_UI_BACKEND_URL_PROD
    : SEGP_UI_BACKEND_URL_STG;

export const DATADOG_TOKEN = 'pub8ea69aae5983526b0e7c5b43fe33f36a';

export const DATADOG_RUM_APPLICATION_ID =
  '1d16d5ca-8a7b-42f7-abde-5df98754aa8f';
